import * as React from "react"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import * as Portal from "@radix-ui/react-portal"

export function Combobox({
  options = [],
  value,
  onValueChange,
  placeholder = "Select or type...",
  emptyText = "No option found.",
  className,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(value || "")
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 })
  const inputRef = React.useRef(null)
  const containerRef = React.useRef(null)
  const dropdownRef = React.useRef(null)

  React.useEffect(() => {
    setInputValue(value || "")
  }, [value])

  // Update dropdown position when opened
  React.useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [open])

  // Handle clicks outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleSelect = (selectedValue) => {
    setInputValue(selectedValue)
    onValueChange(selectedValue)
    setOpen(false)
    inputRef.current?.focus()
  }

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onValueChange(newValue)
    if (!open) setOpen(true)
  }

  const handleFocus = () => {
    setOpen(true)
  }

  // Filter options based on input
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  )

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("h-10 bg-secondary/50 pr-8", className)}
        />
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
      </div>
      {open && filteredOptions.length > 0 && (
        <Portal.Root>
          <div 
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 9999
            }}
            className="mt-1 bg-popover border rounded-md shadow-lg max-h-[250px] overflow-auto"
            onMouseDown={(e) => e.preventDefault()}
          >
            {filteredOptions.map((option) => (
              <div
                key={option}
                onClick={() => handleSelect(option)}
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground px-3 py-2 flex items-center"
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option ? "opacity-100" : "opacity-0"
                  )}
                />
                <span>{option}</span>
              </div>
            ))}
          </div>
        </Portal.Root>
      )}
    </div>
  )
}
